/**
 * Keep the screen awake while an encode or upload is running.
 *
 * A phone that locks its screen suspends the tab, and a suspended tab is a
 * suspended worker: the encode stops, the chunk in flight never settles, and
 * the contributor comes back to a job frozen partway with no way to tell
 * whether it is working or dead. Phones lock after 30 seconds by default and
 * these jobs take minutes, so it is not an edge case - it is the normal
 * outcome of putting the phone down.
 *
 * The Screen Wake Lock API exists for exactly this. It is a request, not a
 * guarantee: the browser drops the lock whenever the page stops being
 * visible, and may refuse it outright on low battery. So the lock is
 * re-acquired on visibilitychange, and every failure is swallowed - a tool
 * that cannot upload because it could not dim-proof a screen would be worse
 * than one that sometimes dims.
 */

interface WakeLockSentinelLike {
  released: boolean
  release: () => Promise<void>
  addEventListener: (type: "release", listener: () => void) => void
}

interface WakeLockLike {
  request: (type: "screen") => Promise<WakeLockSentinelLike>
}

function wakeLockApi(): WakeLockLike | null {
  const nav = navigator as Navigator & { wakeLock?: WakeLockLike }
  return nav.wakeLock ?? null
}

/** Whether this browser offers the API at all. Chrome, Edge, Safari 16.4+. */
export function supportsWakeLock(): boolean {
  return wakeLockApi() !== null
}

let sentinel: WakeLockSentinelLike | null = null
/** Whether callers currently want the screen held awake. */
let wanted = false

async function acquire(): Promise<void> {
  const api = wakeLockApi()
  if (!api || sentinel || !wanted) return
  try {
    const next = await api.request("screen")
    // Another release may have landed while the request was in flight.
    if (!wanted) { try { await next.release() } catch { /* ignore */ } ; return }
    sentinel = next
    next.addEventListener("release", () => { sentinel = null })
  } catch {
    // Denied, usually because the page is hidden or the battery is low.
    // Nothing to do; visibilitychange will try again.
  }
}

function onVisibilityChange(): void {
  // The browser drops the lock whenever the page is hidden. Coming back to
  // the tab has to put it back, or a single glance at another app leaves the
  // rest of a twenty-minute encode unprotected.
  if (document.visibilityState === "visible") void acquire()
}

/**
 * Ask for the screen to stay on. Safe to call repeatedly; the second call
 * while a lock is held does nothing.
 */
export async function requestWakeLock(): Promise<void> {
  if (wanted) return
  wanted = true
  document.addEventListener("visibilitychange", onVisibilityChange)
  await acquire()
}

/** Let the screen sleep again. Safe to call when no lock is held. */
export async function releaseWakeLock(): Promise<void> {
  wanted = false
  document.removeEventListener("visibilitychange", onVisibilityChange)
  const held = sentinel
  sentinel = null
  if (held && !held.released) {
    try { await held.release() } catch { /* already gone */ }
  }
}

/** Test seam: whether a lock is currently held. */
export function wakeLockHeld(): boolean {
  return sentinel !== null
}
